import { useEffect, useState, useCallback } from 'react';

/**
 * Security utilities for anti-surveillance protection
 * These measures make automated screen capture and scraping significantly harder
 */

// Detect if window is focused
export const useWindowFocus = () => {
  const [isFocused, setIsFocused] = useState(true);

  useEffect(() => {
    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);
    const handleVisibility = () => setIsFocused(!document.hidden);

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return isFocused;
};

// Detect DevTools open (potential inspection)
export const useDevToolsDetection = () => {
  const [isDevToolsOpen, setIsDevToolsOpen] = useState(false);

  useEffect(() => {
    const threshold = 160;
    const checkDevTools = () => {
      const widthThreshold = window.outerWidth - window.innerWidth > threshold;
      const heightThreshold = window.outerHeight - window.innerHeight > threshold;
      setIsDevToolsOpen(widthThreshold || heightThreshold);
    };

    checkDevTools();
    window.addEventListener('resize', checkDevTools);
    
    // Also check periodically
    const interval = setInterval(checkDevTools, 1000);

    return () => {
      window.removeEventListener('resize', checkDevTools);
      clearInterval(interval);
    };
  }, []);

  return isDevToolsOpen;
};

// Generate watermark with user info (deters sharing)
export const generateWatermark = (userId, username) => {
  const timestamp = new Date().toISOString().slice(0, 10);
  return `${username?.slice(0, 3) || '***'}•${userId?.slice(-4) || '****'}•${timestamp}`;
};

// Prevent right-click context menu on sensitive elements
export const usePreventContextMenu = (ref) => {
  useEffect(() => {
    const element = ref?.current;
    if (!element) return;

    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    element.addEventListener('contextmenu', handleContextMenu);
    return () => element.removeEventListener('contextmenu', handleContextMenu);
  }, [ref]);
};

// Prevent keyboard shortcuts for screenshots
export const usePreventScreenshotShortcuts = () => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Prevent PrintScreen
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        return false;
      }
      // Prevent Cmd+Shift+3/4 on Mac (screenshot shortcuts)
      if (e.metaKey && e.shiftKey && (e.key === '3' || e.key === '4')) {
        e.preventDefault();
        return false;
      }
      // Prevent Ctrl+Shift+S (some screenshot tools)
      if (e.ctrlKey && e.shiftKey && e.key === 'S') {
        e.preventDefault();
        return false;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
};

// Hook for hover-to-reveal messages
export const useHoverReveal = () => {
  const [hoveredId, setHoveredId] = useState(null);
  
  const handleMouseEnter = useCallback((id) => {
    setHoveredId(id);
  }, []);
  
  const handleMouseLeave = useCallback(() => {
    setHoveredId(null);
  }, []);
  
  const isRevealed = useCallback((id) => {
    return hoveredId === id;
  }, [hoveredId]);
  
  return { handleMouseEnter, handleMouseLeave, isRevealed, hoveredId };
};

// Security status component data
export const getSecurityStatus = (isFocused, isDevToolsOpen) => {
  if (isDevToolsOpen) {
    return {
      level: 'warning',
      message: 'Developer tools detected',
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/10',
    };
  }
  if (!isFocused) {
    return {
      level: 'protected',
      message: 'Content hidden - window not focused',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
    };
  }
  return {
    level: 'secure',
    message: 'Secure viewing mode',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
  };
};
